import { supabase } from '@/lib/supabase';
import { Enrollment } from '@/types/classTypes';
import { handleError, ErrorSeverity } from '@/utils/errorHandler';

/**
 * Service responsible for managing class enrollments
 */
export const EnrollmentService = {
  /**
   * Fetches all enrollments for a specific class
   */
  async getClassEnrollments(classId: string): Promise<Enrollment[]> {
    try {
      const { data, error } = await supabase
        .from('enrollments')
        .select(`
          *,
          student:students!enrollments_student_id_fkey(*)
        `)
        .eq('class_id', classId);
      
      if (error) throw error;
      return (data || []).map(item => ({
        ...item,
        status: (item.status || 'active') as 'active' | 'withdrawn' | 'waitlisted'
      })) as Enrollment[];
    } catch (error) {
      handleError(
        error,
        'Error fetching class enrollments', 
        ErrorSeverity.WARNING,
        { additionalInfo: { classId } }
      );
      return [];
    }
  },

  /**
   * Enrolls a single student in a class with capacity and waitlist checking
   */
  async enrollStudent(enrollmentData: {
    class_id: string;
    student_id: string;
    organization_id: string;
    status?: 'active' | 'withdrawn' | 'waitlist';
    enrolled_by?: string;
  }): Promise<Enrollment> {
    try {
      // If status is not active, use direct insert (for admin overrides)
      if (enrollmentData.status && enrollmentData.status !== 'active') {
        const { data, error } = await supabase
          .from('enrollments')
          .insert({
            ...enrollmentData,
            status: enrollmentData.status
          })
          .select()
          .single();
        
        if (error) throw error;
        return {
          ...data,
          status: data.status as 'active' | 'withdrawn' | 'waitlisted'
        } as Enrollment;
      }

      // For active enrollments, use the safe enrollment function
      const { data: enrollmentResult, error: enrollmentError } = await supabase
        .rpc('manage_enrollment_with_waitlist', {
          p_class_id: enrollmentData.class_id,
          p_student_id: enrollmentData.student_id,
          p_organization_id: enrollmentData.organization_id,
          p_enrolled_by: enrollmentData.enrolled_by || null
        });

      if (enrollmentError) throw enrollmentError;

      const result = enrollmentResult as {
        success: boolean;
        status: string;
        message: string;
        enrollment_id?: string;
        waitlist_entry_id?: string;
      };

      if (!result.success) {
        throw new Error(result.message);
      }

      if (result.status === 'enrolled' && result.enrollment_id) {
        // Fetch the complete enrollment data
        const { data: enrollmentData, error: fetchError } = await supabase
          .from('enrollments')
          .select('*')
          .eq('id', result.enrollment_id)
          .single();

        if (fetchError) throw fetchError;

        return {
          ...enrollmentData,
          status: enrollmentData.status as 'active' | 'withdrawn' | 'waitlisted'
        } as Enrollment;
      }

      // If waitlisted, throw an error with specific message
      if (result.status === 'waitlisted') {
        throw new Error(`Class is full. Student has been added to the waitlist.`);
      }

      throw new Error(result.message || 'Failed to enroll student');

    } catch (error) {
      handleError(
        error,
        'Error enrolling student', 
        ErrorSeverity.ERROR,
        { 
          additionalInfo: { 
            classId: enrollmentData.class_id,
            studentId: enrollmentData.student_id 
          } 
        }
      );
      throw error;
    }
  },

  /**
   * Removes a student enrollment
   */
  async unenrollStudent(enrollmentId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('enrollments')
        .delete()
        .eq('id', enrollmentId);
      
      if (error) throw error;
      return true;
    } catch (error) {
      handleError(
        error,
        'Error unenrolling student', 
        ErrorSeverity.ERROR,
        { additionalInfo: { enrollmentId } }
      );
      throw error;
    }
  },

  /**
   * Alias for unenrollStudent for backward compatibility
   */
  async removeEnrollment(enrollmentId: string): Promise<boolean> {
    return this.unenrollStudent(enrollmentId);
  },

  /**
   * Creates multiple enrollments at once with capacity checking
   */
  async createEnrollments(enrollments: Array<{
    class_id: string;
    student_id: string;
    organization_id: string;
    status?: 'active' | 'withdrawn' | 'waitlisted';
    enrolled_by?: string;
  }>): Promise<boolean> {
    try {
      // Process each enrollment individually to ensure proper capacity checking
      const results = [];
      
      for (const enrollment of enrollments) {
        try {
          // If status is not active, use direct insert (for admin overrides)
          if (enrollment.status && enrollment.status !== 'active') {
            const { data, error } = await supabase
              .from('enrollments')
              .insert({
                ...enrollment,
                status: enrollment.status
              })
              .select()
              .single();
            
            if (error) throw error;
            results.push({ success: true, data });
          } else {
            // For active enrollments, use the safe enrollment function
            const { data: enrollmentResult, error: enrollmentError } = await supabase
              .rpc('manage_enrollment_with_waitlist', {
                p_class_id: enrollment.class_id,
                p_student_id: enrollment.student_id,
                p_organization_id: enrollment.organization_id,
                p_enrolled_by: enrollment.enrolled_by || null
              });

            if (enrollmentError) throw enrollmentError;

            const result = enrollmentResult as {
              success: boolean;
              status: string;
              message: string;
              enrollment_id?: string;
              waitlist_entry_id?: string;
            };

            results.push({ success: result.success, result });
          }
        } catch (error) {
          console.error(`Failed to enroll student ${enrollment.student_id} in class ${enrollment.class_id}:`, error);
          results.push({ success: false, error });
        }
      }
      
      // Check if all enrollments succeeded
      const allSucceeded = results.every(r => r.success);
      
      if (!allSucceeded) {
        const failedCount = results.filter(r => !r.success).length;
        throw new Error(`${failedCount} out of ${enrollments.length} enrollments failed`);
      }
      
      return true;
    } catch (error) {
      handleError(
        error,
        'Error creating multiple enrollments', 
        ErrorSeverity.ERROR,
        { additionalInfo: { enrollmentsCount: enrollments.length } }
      );
      return false;
    }
  }
};
